import React, { useState, useEffect } from 'react';
import { 
  Star, 
  Search, 
  Filter, 
  MoreHorizontal, 
  Trash2, 
  MessageSquare, 
  AlertTriangle,
  CheckCircle,
  XCircle,
  Plus,
  RefreshCw,
  Calendar,
  User,
  ShoppingBag
} from 'lucide-react';
import api from '../../utils/api';
import { Review, ReviewsResponse, Product } from '../../types';
import ProductImage from '../../components/ProductImage';
import TEXT_CONSTANTS from '../../constants/text';

const ReviewManagement: React.FC = () => {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: '',
    rating: '',
    reported: '',
    product: '',
    sortBy: 'createdAt',
    sortOrder: 'desc'
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedReview, setSelectedReview] = useState<Review | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAddReviewModal, setShowAddReviewModal] = useState(false);
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [adminResponse, setAdminResponse] = useState('');
  const [products, setProducts] = useState<Product[]>([]);
  const [statistics, setStatistics] = useState({
    total: 0,
    averageRating: 0,
    ratingDistribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }
  });

  // Add new review form state
  const [newReview, setNewReview] = useState({
    productId: '',
    userId: '',
    rating: 5,
    comment: '',
    isVerifiedPurchase: false
  });

  useEffect(() => {
    fetchReviews();
    fetchProducts();
  }, [filters, currentPage]);

  const fetchReviews = async () => {
    try {
      setIsLoading(true);
      const queryParams = new URLSearchParams({
        page: currentPage.toString(),
        limit: '10',
        ...filters
      });

      const response = await api.get<ReviewsResponse>(`/admin/reviews?${queryParams.toString()}`);
      setReviews(response.data.reviews || []);
      setTotalPages(response.data.totalPages || 1);
      setStatistics({
        total: response.data.total || 0,
        averageRating: response.data.averageRating || 0,
        ratingDistribution: response.data.ratingDistribution || { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }
      });
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      console.log('Fetching products for dropdown...');
      const response = await api.get('/products?limit=1000&active=true');
      console.log('Products response:', response.data);
      setProducts(response.data.products || response.data || []);
    } catch (error) {
      console.error('Error fetching products:', error);
      // Try fallback endpoint
      try {
        const fallbackResponse = await api.get('/products');
        console.log('Fallback products response:', fallbackResponse.data);
        setProducts(fallbackResponse.data.products || fallbackResponse.data || []);
      } catch (fallbackError) {
        console.error('Error with fallback products fetch:', fallbackError);
      }
    }
  };

  const handleDeleteReview = async (reviewId: string) => {
    try {
      await api.delete(`/admin/reviews/${reviewId}`);
      fetchReviews();
      setShowDeleteModal(false);
      setSelectedReview(null);
    } catch (error) {
      console.error('Error deleting review:', error);
    }
  };

  const handleAddReview = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.post('/admin/reviews', newReview);
      fetchReviews();
      setShowAddReviewModal(false);
      setNewReview({
        productId: '',
        userId: '',
        rating: 5,
        comment: '',
        isVerifiedPurchase: false
      });
    } catch (error) {
      console.error('Error adding review:', error);
    }
  };

  const handleAdminResponse = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedReview) return;

    try {
      await api.post(`/admin/reviews/${selectedReview._id}/response`, {
        message: adminResponse
      });
      fetchReviews();
      setShowResponseModal(false);
      setAdminResponse('');
      setSelectedReview(null);
    } catch (error) {
      console.error('Error adding admin response:', error);
    }
  };

  const toggleReportedStatus = async (reviewId: string, reported: boolean) => {
    try {
      await api.patch(`/admin/reviews/${reviewId}`, { reported: !reported });
      fetchReviews();
    } catch (error) {
      console.error('Error updating review status:', error);
    }
  };

  const renderStars = (rating: number, size: 'sm' | 'md' | 'lg' = 'sm') => {
    const sizeClasses = {
      sm: 'w-4 h-4',
      md: 'w-5 h-5',
      lg: 'w-6 h-6'
    };

    return (
      <div className="flex">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`${sizeClasses[size]} ${
              star <= rating
                ? 'text-yellow-400 fill-current'
                : 'text-gray-300 dark:text-gray-600'
            }`}
          />
        ))}
      </div>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Review Management
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Manage customer reviews and ratings
          </p>
        </div>
        <button
          onClick={() => setShowAddReviewModal(true)}
          className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg font-medium 
                   transition-colors flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Add Review
        </button>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        <div className="bg-white dark:bg-secondary-800 rounded-lg p-4 sm:p-6 border border-gray-200 dark:border-secondary-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Reviews</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{statistics.total}</p>
            </div>
            <MessageSquare className="w-8 h-8 text-blue-600" />
          </div>
        </div>

        <div className="bg-white dark:bg-secondary-800 rounded-lg p-4 sm:p-6 border border-gray-200 dark:border-secondary-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Average Rating</p>
              <div className="flex items-center gap-2">
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {statistics.averageRating.toFixed(1)}
                </p>
                <Star className="w-5 h-5 text-yellow-400 fill-current" />
              </div>
            </div>
            <Star className="w-8 h-8 text-yellow-600" />
          </div>
        </div>

        <div className="bg-white dark:bg-secondary-800 rounded-lg p-4 sm:p-6 border border-gray-200 dark:border-secondary-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">5-Star Reviews</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {statistics.ratingDistribution[5]}
              </p>
            </div>
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
        </div>

        <div className="bg-white dark:bg-secondary-800 rounded-lg p-4 sm:p-6 border border-gray-200 dark:border-secondary-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">1-Star Reviews</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {statistics.ratingDistribution[1]}
              </p>
            </div>
            <XCircle className="w-8 h-8 text-red-600" />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-secondary-800 rounded-lg p-4 sm:p-6 border border-gray-200 dark:border-secondary-700">
        <div className="space-y-4">
          {/* Search Row */}
          <div className="w-full">
            <div className="relative">
              <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search reviews, products, or users..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 dark:border-secondary-600 
                         bg-white dark:bg-secondary-900 text-gray-900 dark:text-gray-100
                         rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-base"
              />
            </div>
          </div>

          {/* Filters Row */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">

            {/* Rating Filter */}
            <select
              value={filters.rating}
              onChange={(e) => setFilters({ ...filters, rating: e.target.value })}
              className="px-3 py-2.5 border border-gray-300 dark:border-secondary-600 
                       bg-white dark:bg-secondary-900 text-gray-900 dark:text-gray-100
                       rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-base"
            >
              <option value="">All Ratings</option>
              <option value="5">5 Stars</option>
              <option value="4">4 Stars</option>
              <option value="3">3 Stars</option>
              <option value="2">2 Stars</option>
              <option value="1">1 Star</option>
            </select>

            {/* Reported Filter */}
            <select
              value={filters.reported}
              onChange={(e) => setFilters({ ...filters, reported: e.target.value })}
              className="px-3 py-2.5 border border-gray-300 dark:border-secondary-600 
                       bg-white dark:bg-secondary-900 text-gray-900 dark:text-gray-100
                       rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-base"
            >
              <option value="">All Reviews</option>
              <option value="true">Reported Only</option>
              <option value="false">Not Reported</option>
            </select>

            {/* Sort */}
            <select
              value={`${filters.sortBy}-${filters.sortOrder}`}
              onChange={(e) => {
                const [sortBy, sortOrder] = e.target.value.split('-');
                setFilters({ ...filters, sortBy, sortOrder });
              }}
              className="px-3 py-2.5 border border-gray-300 dark:border-secondary-600 
                       bg-white dark:bg-secondary-900 text-gray-900 dark:text-gray-100
                       rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-base"
            >
              <option value="createdAt-desc">Newest First</option>
              <option value="createdAt-asc">Oldest First</option>
              <option value="rating-desc">Highest Rating</option>
              <option value="rating-asc">Lowest Rating</option>
              <option value="helpful-desc">Most Helpful</option>
            </select>

            {/* Refresh Button */}
            <button
              onClick={fetchReviews}
              className="px-4 py-2.5 bg-gray-100 hover:bg-gray-200 dark:bg-secondary-700 dark:hover:bg-secondary-600
                       text-gray-700 dark:text-gray-300 rounded-lg transition-colors flex items-center justify-center gap-2
                       touch-manipulation min-h-[44px]"
            >
              <RefreshCw className="w-4 h-4 flex-shrink-0" />
              <span className="hidden sm:inline">Refresh</span>
            </button>
          </div>
        </div>
      </div>

      {/* Reviews List */}
      <div className="bg-white dark:bg-secondary-800 rounded-lg border border-gray-200 dark:border-secondary-700">
        {isLoading ? (
          <div className="flex items-center justify-center p-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : reviews.length === 0 ? (
          <div className="text-center py-8">
            <MessageSquare className="w-12 h-12 text-gray-400 dark:text-gray-600 mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400">No reviews found</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200 dark:divide-secondary-700">
            {reviews.map((review) => (
              <div key={review._id} className="p-4 sm:p-6">
                <div className="space-y-4">
                  {/* Mobile Header - Product Info & Actions */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <ProductImage
                        src={review.product.images[0]}
                        alt={review.product.name}
                        className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg object-cover flex-shrink-0"
                      />
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-gray-900 dark:text-gray-100 truncate text-sm sm:text-base">
                          {review.product.name}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          {renderStars(review.rating, 'sm')}
                          <span className="text-xs sm:text-sm font-medium text-gray-900 dark:text-gray-100">
                            {review.rating}/5
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Actions Menu */}
                    <button
                      onClick={() => setSelectedReview(review)}
                      className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-2 
                               touch-manipulation min-h-[44px] min-w-[44px] flex items-center justify-center"
                    >
                      <MoreHorizontal className="w-5 h-5" />
                    </button>
                  </div>

                  {/* Badges Row */}
                  <div className="flex flex-wrap gap-2">
                    {review.isVerifiedPurchase && (
                      <span className="px-2 py-1 bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-400 
                                   text-xs rounded-full font-medium">
                        Verified
                      </span>
                    )}
                    {review.reported && (
                      <span className="px-2 py-1 bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-400 
                                   text-xs rounded-full font-medium">
                        Reported
                      </span>
                    )}
                  </div>

                  {/* Review Comment */}
                  <div className="space-y-3">
                    <p className="text-gray-700 dark:text-gray-300 text-sm sm:text-base leading-relaxed">
                      {review.comment}
                    </p>

                    {/* Admin Response */}
                    {review.adminResponse && (
                      <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-400 p-3 mb-3">
                        <p className="text-sm font-medium text-blue-800 dark:text-blue-300 mb-1">
                          Admin Response:
                        </p>
                        <p className="text-blue-700 dark:text-blue-300 text-sm">
                          {review.adminResponse.message}
                        </p>
                        <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                          {formatDate(review.adminResponse.respondedAt)}
                        </p>
                      </div>
                    )}

                    {/* Review Meta */}
                    <div className="flex flex-wrap items-center gap-3 sm:gap-4 text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                      <div className="flex items-center gap-1">
                        <User className="w-3 h-3 sm:w-4 sm:h-4" />
                        <span className="truncate max-w-[100px] sm:max-w-none">{review.user.name}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3 h-3 sm:w-4 sm:h-4" />
                        <span className="hidden sm:inline">{formatDate(review.createdAt)}</span>
                        <span className="sm:hidden">
                          {new Date(review.createdAt).toLocaleDateString('en-IN', { 
                            month: 'short', 
                            day: 'numeric' 
                          })}
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4" />
                        {review.helpful} helpful
                      </div>
                    </div>
                  </div>

                  {/* Quick Actions */}
                  {selectedReview?._id === review._id && (
                    <div className="mt-4 pt-4 border-t border-gray-200 dark:border-secondary-700">
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3">
                        <button
                          onClick={() => setShowResponseModal(true)}
                          className="px-3 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg 
                                   transition-colors flex items-center justify-center gap-2 touch-manipulation min-h-[44px]"
                        >
                          <MessageSquare className="w-4 h-4" />
                          <span className="hidden sm:inline">
                            {review.adminResponse ? 'Update Response' : 'Add Response'}
                          </span>
                          <span className="sm:hidden">Response</span>
                        </button>
                        
                        <button
                          onClick={() => toggleReportedStatus(review._id, review.reported)}
                          className={`px-3 py-2.5 text-sm rounded-lg transition-colors flex items-center justify-center gap-2
                                     touch-manipulation min-h-[44px] ${review.reported 
                                      ? 'bg-green-600 hover:bg-green-700 text-white' 
                                      : 'bg-orange-600 hover:bg-orange-700 text-white'
                                    }`}
                        >
                          <AlertTriangle className="w-4 h-4" />
                          <span className="hidden sm:inline">
                            {review.reported ? 'Remove Report' : 'Mark as Reported'}
                          </span>
                          <span className="sm:hidden">
                            {review.reported ? 'Unreport' : 'Report'}
                          </span>
                        </button>
                        
                        <button
                          onClick={() => setShowDeleteModal(true)}
                          className="px-3 py-2.5 bg-red-600 hover:bg-red-700 text-white text-sm rounded-lg 
                                   transition-colors flex items-center justify-center gap-2 touch-manipulation min-h-[44px]"
                        >
                          <Trash2 className="w-4 h-4" />
                          Delete
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-2 p-4 border-t border-gray-200 dark:border-secondary-700">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="px-4 py-2.5 text-sm border border-gray-300 dark:border-secondary-600 rounded-lg 
                       disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-secondary-700
                       transition-colors touch-manipulation min-h-[44px]"
            >
              <span className="hidden sm:inline">Previous</span>
              <span className="sm:hidden">Prev</span>
            </button>
            
            <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 px-2">
              <span className="hidden sm:inline">Page {currentPage} of {totalPages}</span>
              <span className="sm:hidden">{currentPage}/{totalPages}</span>
            </span>
            
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="px-4 py-2.5 text-sm border border-gray-300 dark:border-secondary-600 rounded-lg 
                       disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-secondary-700
                       transition-colors touch-manipulation min-h-[44px]"
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedReview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-secondary-800 rounded-t-lg sm:rounded-lg p-4 sm:p-6 w-full max-w-md 
                         max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Delete Review
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6 text-sm sm:text-base">
              Are you sure you want to delete this review? This action cannot be undone.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setSelectedReview(null);
                }}
                className="w-full sm:w-auto px-4 py-2.5 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-secondary-600 
                         rounded-lg hover:bg-gray-50 dark:hover:bg-secondary-700 transition-colors touch-manipulation min-h-[44px]"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteReview(selectedReview?._id || '')}
                className="w-full sm:w-auto px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors
                         touch-manipulation min-h-[44px]"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Admin Response Modal */}
      {showResponseModal && selectedReview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-secondary-800 rounded-t-lg sm:rounded-lg p-4 sm:p-6 w-full max-w-lg 
                         max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              {selectedReview?.adminResponse ? 'Update Admin Response' : 'Add Admin Response'}
            </h3>
            
            <form onSubmit={handleAdminResponse}>
              <textarea
                value={adminResponse || selectedReview?.adminResponse?.message || ''}
                onChange={(e) => setAdminResponse(e.target.value)}
                placeholder="Enter your response to this review..."
                rows={4}
                required
                className="w-full px-3 py-2 border border-gray-300 dark:border-secondary-600 
                         bg-white dark:bg-secondary-900 text-gray-900 dark:text-gray-100
                         rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent mb-4"
              />
              
              <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
                <button
                  type="button"
                  onClick={() => {
                    setShowResponseModal(false);
                    setAdminResponse('');
                    setSelectedReview(null);
                  }}
                  className="w-full sm:w-auto px-4 py-2.5 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-secondary-600 
                           rounded-lg hover:bg-gray-50 dark:hover:bg-secondary-700 transition-colors touch-manipulation min-h-[44px]"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="w-full sm:w-auto px-4 py-2.5 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors
                           touch-manipulation min-h-[44px]"
                >
                  {selectedReview?.adminResponse ? 'Update Response' : 'Add Response'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Review Modal */}
      {showAddReviewModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-secondary-800 rounded-t-lg sm:rounded-lg p-4 sm:p-6 w-full max-w-lg 
                         max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Add New Review
            </h3>
            
            <form onSubmit={handleAddReview} className="space-y-4">
              {/* Product Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Product
                </label>
                <select
                  value={newReview.productId}
                  onChange={(e) => setNewReview({ ...newReview, productId: e.target.value })}
                  required
                  className="w-full px-3 py-2 border border-gray-300 dark:border-secondary-600 
                           bg-white dark:bg-secondary-900 text-gray-900 dark:text-gray-100
                           rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="">Select a product</option>
                  {products.map((product) => (
                    <option key={product._id} value={product._id}>
                      {product.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* User ID */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  User ID (optional)
                </label>
                <input
                  type="text"
                  value={newReview.userId}
                  onChange={(e) => setNewReview({ ...newReview, userId: e.target.value })}
                  placeholder="Enter user ID or leave empty for admin review"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-secondary-600 
                           bg-white dark:bg-secondary-900 text-gray-900 dark:text-gray-100
                           rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              {/* Rating */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Rating
                </label>
                <div className="flex items-center gap-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setNewReview({ ...newReview, rating: star })}
                      className="focus:outline-none"
                    >
                      <Star
                        className={`w-6 h-6 ${
                          star <= newReview.rating
                            ? 'text-yellow-400 fill-current'
                            : 'text-gray-300 dark:text-gray-600'
                        }`}
                      />
                    </button>
                  ))}
                  <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">
                    {newReview.rating}/5
                  </span>
                </div>
              </div>

              {/* Comment */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Review Comment
                </label>
                <textarea
                  value={newReview.comment}
                  onChange={(e) => setNewReview({ ...newReview, comment: e.target.value })}
                  placeholder="Enter review comment..."
                  rows={4}
                  required
                  className="w-full px-3 py-2 border border-gray-300 dark:border-secondary-600 
                           bg-white dark:bg-secondary-900 text-gray-900 dark:text-gray-100
                           rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              {/* Verified Purchase */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="verifiedPurchase"
                  checked={newReview.isVerifiedPurchase}
                  onChange={(e) => setNewReview({ ...newReview, isVerifiedPurchase: e.target.checked })}
                  className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <label htmlFor="verifiedPurchase" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                  Mark as verified purchase
                </label>
              </div>

              <div className="flex gap-3 justify-end pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddReviewModal(false);
                    setNewReview({
                      productId: '',
                      userId: '',
                      rating: 5,
                      comment: '',
                      isVerifiedPurchase: false
                    });
                  }}
                  className="w-full sm:w-auto px-4 py-2.5 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-secondary-600 
                           rounded-lg hover:bg-gray-50 dark:hover:bg-secondary-700 transition-colors touch-manipulation min-h-[44px]"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="w-full sm:w-auto px-4 py-2.5 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors
                           touch-manipulation min-h-[44px]"
                >
                  Add Review
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReviewManagement;